<script setup>
import { computed, onMounted, ref } from 'vue'
import { useCamerasStore } from '@/store/cameras'
import { humanReadableDuration } from '@/composables/datetime'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import CameraSelect from '@/components/CameraSelect.vue'

const { t } = useI18n()
const toast = useToast()
const camerasStore = useCamerasStore()

const props = defineProps({
  preselectedCameras: { type: [Array, Object, String], default: () => [] },
  readOnlySelection: { type: Boolean, default: true },
})

const selectedCameras = ref([])
const privacyDuration = ref(1000 * 60 * 60)
const loading = ref(false)

const availableCameras = computed(() => camerasStore.getNonDecommissionedCameras())

const camerasInPrivacyMode = computed(() => {
  return availableCameras.value.filter(camera => camera.privacy)
})

const singleCamera = computed(() => {
  return selectedCameras.value.length === 1 && props.readOnlySelection === true
})

const durations = [
  1000,
  1000 * 60 * 10,
  1000 * 60 * 20,
  1000 * 60 * 30,
  1000 * 60 * 60,
  1000 * 60 * 60 * 3,
  1000 * 60 * 60 * 6,
  1000 * 60 * 60 * 24,
  1000 * 60 * 60 * 24 * 7,
]

const durationItems = durations.map(duration => {
  return {
    title: humanReadableDuration(duration),
    value: duration,
  }
})

async function setPrivacy() {
  loading.value = true
  const cameraIds = selectedCameras.value.map(camera => camera.cameraId)
  await camerasStore.setPrivacyForMultipleCameras(cameraIds, privacyDuration.value)
  toast.success(t('camera_privacy.privacy_activated_success'))
  await loadCameras()
}

async function unsetPrivacy() {
  loading.value = true
  const cameraIds = camerasInPrivacyMode.value.map(camera => camera.cameraId)
  await camerasStore.setPrivacyForMultipleCameras(cameraIds, false)
  toast.success(t('camera_privacy.privacy_deactivated_success'))
  await loadCameras()
}

async function loadCameras() {
  loading.value = true
  await camerasStore.loadCameras()
  loading.value = false
}

onMounted(async () => {
  selectedCameras.value = props.preselectedCameras
  await camerasStore.ensureCamerasLoaded()
})
</script>

<template>
  <v-card
    variant="flat"
    :disabled="loading"
    :loading="loading"
    class="d-flex flex-column align-stretch justify-center overflow-visible"
  >
    <div class="px-4 d-flex align-center ga-2 text-truncate">
      <v-icon icon="mdi-incognito" />
      <v-card-title
        v-if="singleCamera"
        class="px-0 flex-shrink-1 text-truncate"
      >
        {{ $t('camera_privacy.title') }}
      </v-card-title>
      <v-card-title
        v-if="!singleCamera"
        class="px-0 flex-shrink-1 text-truncate"
      >
        {{ $t('camera_privacy.title') }}
      </v-card-title>
    </div>

    <v-card
      v-if="camerasInPrivacyMode.length > 0"
      variant="flat"
      class="ma-2"
    >
      <v-card-title>{{ $t('camera_privacy.deactivate_title') }}</v-card-title>
      <v-card-text class="text-center">
        {{ $t('camera_privacy.deactivate_text', { count: camerasInPrivacyMode.length }) }}
      </v-card-text>
      <v-card-actions class="justify-center">
        <v-btn
          prepend-icon="mdi-cctv"
          variant="outlined"
          @click="unsetPrivacy"
        >
          {{ $t('camera_privacy.deactivate_button') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <p
      v-if="singleCamera"
      class="px-4 text-subtitle-1 font-weight-medium"
    >
      {{ $t('camera_privacy.camera', { cameraName: selectedCameras[0].name }) }}
    </p>

    <p
      v-if="!singleCamera"
      class="px-4 text-subtitle-1 font-weight-medium text-center"
    >
      {{ $t('camera_privacy.cameras', { cameraCount: selectedCameras.length }) }}
    </p>

    <div class="my-4 px-4 w-100 d-flex flex-column align-center ga-4">
      <CameraSelect
        v-if="!singleCamera"
        v-model:selected-cameras="selectedCameras"
        :read-only-selection="props.readOnlySelection"
        :available-cameras="availableCameras"
        multiple
      />

      <v-autocomplete
        v-model="privacyDuration"
        prepend-icon="mdi-timer"
        :label="$t('camera_privacy.duration')"
        variant="outlined"
        :items="durationItems"
        item-title="title"
        item-value="value"
        width="100%"
        hide-details
      />
      <v-btn
        prepend-icon="mdi-cctv-off"
        color="primary"
        variant="flat"
        width="fit-content"
        @click="setPrivacy"
      >
        {{ $t('camera_privacy.activate_button') }}
      </v-btn>
    </div>
  </v-card>
</template>
